const STORAGE_PREFIX = 'persona-filters';
const ACTIVE_FILTER_PREFIX = 'persona-active-filters';

function storageKey(domain) {
  return `${STORAGE_PREFIX}:${domain || 'default'}`;
}

function activeFilterKey(domain) {
  return `${ACTIVE_FILTER_PREFIX}:${domain || 'default'}`;
}

/**
 * S4: active filters live in localStorage so they survive closing the tab.
 * Migrates any leftover sessionStorage payload once.
 */
export function saveActiveFilterState(domain, { criteria = [], isGeneratedView = false } = {}) {
  try {
    const payload = JSON.stringify({ criteria, isGeneratedView });
    localStorage.setItem(activeFilterKey(domain), payload);
    try {
      sessionStorage.removeItem(activeFilterKey(domain));
    } catch {
      // Ignore session cleanup errors
    }
  } catch {
    // Ignore storage errors (private mode, quota, etc.)
  }
}

export function loadActiveFilterState(domain) {
  try {
    const key = activeFilterKey(domain);
    let raw = localStorage.getItem(key);
    if (!raw) {
      raw = sessionStorage.getItem(key);
      if (raw) {
        // One-time migrate session → local so a mid-session upgrade keeps filters.
        try {
          localStorage.setItem(key, raw);
          sessionStorage.removeItem(key);
        } catch {
          // Keep reading from session for this load if migrate fails
        }
      }
    }
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return {
      criteria: Array.isArray(parsed?.criteria) ? parsed.criteria : [],
      isGeneratedView: Boolean(parsed?.isGeneratedView),
    };
  } catch {
    return null;
  }
}

export function clearActiveFilterState(domain) {
  try {
    localStorage.removeItem(activeFilterKey(domain));
  } catch {
    // Ignore storage errors
  }
  try {
    sessionStorage.removeItem(activeFilterKey(domain));
  } catch {
    // Ignore storage errors
  }
}

export function buildDefaultFilterName(domain) {
  const now = new Date();
  const date = now.toISOString().slice(0, 10);
  const time = now.toTimeString().slice(0, 8).replace(/:/g, '');
  return `${domain || 'default'}_${date}_${time}`;
}

export function loadFilterPresets(domain) {
  try {
    const raw = localStorage.getItem(storageKey(domain));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function saveFilterPreset(domain, name, criteria) {
  const presets = loadFilterPresets(domain);
  const preset = {
    id: `${Date.now()}`,
    name: name.trim(),
    criteria,
    savedAt: new Date().toISOString(),
  };

  const existingIndex = presets.findIndex((item) => item.name === preset.name);
  if (existingIndex >= 0) {
    presets.splice(existingIndex, 1, preset);
  } else {
    presets.unshift(preset);
  }

  localStorage.setItem(storageKey(domain), JSON.stringify(presets));
  return preset;
}

export function deleteFilterPreset(domain, presetId) {
  const presets = loadFilterPresets(domain).filter((item) => item.id !== presetId);
  localStorage.setItem(storageKey(domain), JSON.stringify(presets));
  return presets;
}
